import { Component,EventEmitter,Input,OnInit,Output,OnDestroy,AfterViewInit,ChangeDetectorRef} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, take} from 'rxjs';
import { IdNameOptions } from 'src/app/core/domain/startup.model';
import { GlobalService } from 'src/app/core/service/global.service';
import { ModalHandlerService } from 'src/app/core/service/modal-handler.service';
import { ProgramOfficeService } from 'src/app/core/service/program-office.service';
interface PaymentDetails {
  name?:string;
  email?:string;
  city?: string;
  country?: string;
  state?: string;
  pincode?: string;
  customerGSTIN?:string;
  address?:string;
  phoneNumber?:number;
  profileName?:string;
}

@Component({
  selector: 'app-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.scss']
})
export class BillingFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() User: any;
  @Output() formDataChange = new EventEmitter<any>();

  billingForm!: FormGroup;
  countries: IdNameOptions[] = [];
  states: IdNameOptions[] = [];
  cities: IdNameOptions[] = [];
  profileData:any;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modalHandler: ModalHandlerService,
    private globalService: GlobalService,
    private cdRef: ChangeDetectorRef,
    private programofficeservice: ProgramOfficeService
  ) {}

  ngOnInit(): void {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      const userId = parsedUser.userId;
      if (userId) {
        this.programofficeservice.userData().subscribe((res) => {
          this.profileData = res;
          this.loadCountries();
        });
      }
    } else {
      this.loadCountries();
    }
    this.initForm();
    this.handleFormChanges();
    this.handleDropdownChanges();
  }

  ngAfterViewInit(): void {
    this.cdRef.detectChanges()
  }

  private initForm(): void {
    this.billingForm = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required,Validators.pattern("^[0-9]*$")]],
      customerGST: [''],
      address: [''],
      country: [null, Validators.required],
      state: [null, Validators.required],
      city: [null, Validators.required],
      pincode: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(10), Validators.pattern(/^[a-zA-Z0-9\s-]+$/)]]
    });
  }

  private handleFormChanges(): void {
    this.billingForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.emitFormData();
      });
  }

  private handleDropdownChanges(): void {
    this.billingForm.get('country')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((countryId: number) => {
        this.resetControls(['state', 'city']);
        if (countryId) this.loadStates(countryId);
      });

    this.billingForm.get('state')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((stateId: number) => {
        this.billingForm.patchValue({ city: null });
        if (stateId) this.loadCities(stateId);
      });
  }

  private emitFormData(): void {
    const value = this.billingForm.value;
    const invalidFields: string[] = [];

    Object.keys(this.billingForm.controls).forEach(field => {
      const control = this.billingForm.get(field);
      if (control?.errors?.['required'] && control.invalid) {
        invalidFields.push(field);
      }
    });

    const payload = {
      ...value,
      countryName: this.getNameById(this.countries, value.country),
      stateName: this.getNameById(this.states, value.state),
      cityName: this.getNameById(this.cities, value.city)
    };

    this.formDataChange.emit({
      payload,
      isValid: this.billingForm.valid,
      invalidRequiredFields: invalidFields
    });
  }

  public highlightInvalidFields(fields: string[]): void {
    fields.forEach(field => {
      const control = this.billingForm.get(field);
      if (control) {
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity({ onlySelf: true });
      }

      const input = document.querySelector(`[formControlName="${field}"]`);
      if (input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  private async patchFormValues(): Promise<void> {
    const paymentDetails: PaymentDetails | {} = this.modalHandler.PAYMENTDETAILS || {};
    const { name,email,city, country, state, pincode, customerGSTIN, address,phoneNumber,profileName} = paymentDetails as PaymentDetails;
    const user = this.modalHandler.CURRENT_USER ?? this.User?.activeUser ?? {};
    const brand = this.modalHandler.BRAND_DETAIL;

    let countryId: number | null = brand?.['registeredCountryR']?.id ?? this.User?.registered_country_id ?? null;
    let stateId: number | null = brand?.['registeredStateR']?.id ?? this.User?.registered_state_id ?? null;
    let cityId: number | null = brand?.['registeredCityR']?.id ?? this.User?.registered_city_id ?? null;

    if (country) {
      const idByName = this.getIdByName(this.countries, country);
      if (idByName) countryId = idByName;
    }

    let matchedStates: IdNameOptions[] = [];
    if (countryId && state) {
      matchedStates = await this.loadStates(countryId);
      const idByName = this.getIdByName(matchedStates, state);
      if (idByName) stateId = idByName;
    }

    let matchedCities: IdNameOptions[] = [];
    if (stateId && city) {
      matchedCities = await this.loadCities(stateId);
      const idByName = this.getIdByName(matchedCities, city);
      if (idByName) cityId = idByName;
    }
    this.billingForm.patchValue({
      fullName: profileName ?? user?.profileName ?? '',
      email: email ?? user?.email ?? this.profileData?.email ??'',
      phone: phoneNumber || user?.mobileNumber || this.profileData?.mobileNumber,
      address: this.User?.address|| address || '',
      country: countryId ? +countryId : null,
      state: stateId ? +stateId : null,
      city: cityId ? +cityId : null,
      pincode: pincode || this.User?.pincode || '',
      customerGST:customerGSTIN || ''
    });

    this.cdRef.detectChanges();
    setTimeout(() => {
      this.emitFormData();
    }, 200);
  }

  private getNameById(list: IdNameOptions[], id: number | null): string {
    return list.find(item => Number(item.id) === Number(id))?.name || '';
  }

  private getIdByName(list: IdNameOptions[], name: string): number | null {
    const found = list.find(item =>
      item.name?.trim().toLowerCase() === name?.trim().toLowerCase()
    );
    return found ? Number(found.id) : null;
  }

  private resetControls(controlNames: string[]): void {
    const resetObj: any = {};
    controlNames.forEach(name => resetObj[name] = null);
    this.billingForm.patchValue(resetObj);
  }

  private async loadCountries(): Promise<void> {
    try {
      const res = await this.globalService.getCountries().pipe(take(1)).toPromise();
      this.countries = res;
      await this.patchFormValues();
    } catch (error) {
     
    }
  }

  private loadStates(countryId: number): Promise<IdNameOptions[]> {
    return new Promise(resolve => {
      this.globalService.getStates(String(countryId))
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          this.states = res;
          if(this.states.length === 0) this.onUpdateValidation(this.states,"state");
          resolve(res);
        });
    });
  }

  private loadCities(stateId: number): Promise<IdNameOptions[]> {
    return new Promise(resolve => {
      this.globalService.getCities(String(stateId))
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          this.cities = res;
          if( this.cities.length === 0) this.onUpdateValidation(this.cities,"city");
          resolve(res);
        });
    });
  }
  onUpdateValidation(value: any, type: string) {
  const control = this.billingForm.get(type);

  if (!value || value.length === 0) {
    control?.clearValidators();
    if(type=="state"){
      this.billingForm.get("city").clearValidators();
      this.cities=[];}
  } else {
    control?.setValidators(Validators.required);
     if(type=="state")this.billingForm.get("city").setValidators(Validators.required);
  }

  control?.updateValueAndValidity();
   if(type=="state")this.billingForm.get("city")?.updateValueAndValidity();
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get f() {
    return this.billingForm.controls;
  }
}
